<template>
    <section class="w-full py-16 md:py-24 lg:py-32 bg-white flex items-center justify-center relative overflow-hidden">

        <!-- Background accents -->
        <div class="absolute top-1/2 left-0 w-80 h-80 bg-eblue-500/5 rounded-full blur-3xl pointer-events-none z-0"></div>

        <div class="container px-4 md:px-6 relative z-10">

            <!-- Header -->
            <div class="text-center mb-16">
                <h2 class="text-3xl md:text-5xl font-bold font-modulus-semi-bold tracking-tighter text-eblack-950 mb-4">
                    {{ $t('how_it_works.title') }}
                </h2>
                <p class="mx-auto max-w-[700px] text-eblack-500 md:text-lg font-modulus">
                    {{ $t('how_it_works.subtitle') }}
                </p>
            </div>

            <!-- Stepper Container -->
            <div class="grid gap-12 lg:grid-cols-12 items-stretch max-w-6xl mx-auto">

                <!-- Left Side: Step List -->
                <div class="lg:col-span-5 flex flex-col justify-between space-y-6">
                    <div
                        v-for="(step, index) in steps"
                        :key="index"
                        @click="activeStep = index"
                        :class="[
                            'group flex items-start gap-4 p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-left',
                            activeStep === index
                                ? 'bg-eorange-50/30 border-eorange-500/30 shadow-md shadow-eorange-500/5'
                                : 'border-transparent hover:bg-ewhite-50/50'
                        ]"
                    >
                        <!-- Step Badge -->
                        <div
                            :class="[
                                'w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-md font-bold font-modulus-semi-bold transition-all duration-300',
                                activeStep === index
                                    ? 'bg-eorange-500 text-white scale-110 shadow-md shadow-eorange-500/20'
                                    : 'bg-ewhite-100 text-eblack-500 group-hover:bg-ewhite-200'
                            ]"
                        >
                            {{ index + 1 }}
                        </div>

                        <!-- Step Info -->
                        <div class="space-y-1 font-modulus">
                            <h3
                                :class="[
                                    'text-lg font-bold transition-colors duration-300',
                                    activeStep === index ? 'text-eorange-500' : 'text-eblack-900'
                                ]"
                            >
                                {{ $t(step.title) }}
                            </h3>
                            <p class="text-sm text-eblack-500 leading-relaxed">
                                {{ $t(step.description) }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Right Side: Morphing Interactive Panel -->
                <div class="lg:col-span-7 flex">
                    <div class="w-full bg-ewhite-50 border border-ewhite-200 rounded-2xl p-6 shadow-xl relative min-h-[420px] flex flex-col justify-between font-modulus">

                        <!-- Mini browser status bar -->
                        <div class="flex items-center justify-between border-b border-ewhite-200 pb-3 mb-4">
                            <div class="flex items-center gap-1.5">
                                <span class="w-2.5 h-2.5 rounded-full bg-eblack-200"></span>
                                <span class="w-2.5 h-2.5 rounded-full bg-eblack-200"></span>
                                <span class="w-2.5 h-2.5 rounded-full bg-eblack-200"></span>
                            </div>
                            <span class="text-xs text-eblack-400 font-semibold bg-white border border-ewhite-200 px-3 py-1 rounded-full">
                                {{ getStepSubHeader() }}
                            </span>
                        </div>

                        <!-- Content morphing according to step -->
                        <div class="flex-grow flex flex-col justify-center">

                            <!-- STEP 1 CONTENT: Live Service Filter (Synced with Simulator) -->
                            <div v-if="activeStep === 0" class="space-y-4 w-full">
                                <div class="flex flex-wrap gap-2 mb-2">
                                    <button
                                        v-for="cat in s1Categories"
                                        :key="cat"
                                        @click="s1SelectedCat = cat"
                                        :class="[
                                            'px-3 py-1 text-xs rounded-full border transition-all font-semibold',
                                            s1SelectedCat === cat
                                                ? 'bg-eorange-500 border-eorange-500 text-white'
                                                : 'bg-white border-ewhite-200 text-eblack-700 hover:border-ewhite-300'
                                        ]"
                                    >
                                        {{ cat }}
                                    </button>
                                </div>
                                <div class="grid gap-3 sm:grid-cols-2">
                                    <div
                                        v-for="service in filteredS1Services"
                                        :key="service.nameKey"
                                        class="bg-white p-3 border border-ewhite-200 rounded-xl flex items-center justify-between shadow-sm animate-fade-in"
                                    >
                                        <div class="flex items-center gap-2">
                                            <span class="text-xl">{{ service.icon }}</span>
                                            <div>
                                                <h5 class="text-xs font-bold text-eblack-900">{{ $t(service.nameKey) }}</h5>
                                                <span class="text-[10px] text-eblack-400 bg-ewhite-100 px-1.5 py-0.5 rounded-full font-semibold">{{ service.category }}</span>
                                            </div>
                                        </div>
                                        <span class="text-xs font-bold text-eorange-500">${{ service.price }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- STEP 2 CONTENT: Simulated Itinerary Builder -->
                            <div v-else-if="activeStep === 1" class="space-y-4 w-full">
                                <div class="relative pl-6 border-l-2 border-ewhite-300 space-y-4">
                                    <div
                                        v-for="(item, index) in s2Itinerary"
                                        :key="index"
                                        :class="[
                                            'relative p-3 bg-white border border-ewhite-200 rounded-xl shadow-xs transition-all duration-500',
                                            item.optimized ? 'border-eblue-400 bg-eblue-50/10' : ''
                                        ]"
                                    >
                                        <!-- Connected Bullet -->
                                        <div
                                            :class="[
                                                'absolute -left-[31px] top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white',
                                                item.optimized ? 'bg-eblue-500' : 'bg-eorange-500'
                                            ]"
                                        >
                                            ✓
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <div>
                                                <span class="text-[10px] font-bold" :class="item.optimized ? 'text-eblue-600' : 'text-eorange-500'">{{ item.time }}</span>
                                                <h5 class="text-xs font-bold text-eblack-900">{{ $t(item.activityKey) }}</h5>
                                            </div>
                                            <span class="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-ewhite-100 text-eblack-500">
                                                {{ item.status }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex justify-end">
                                    <button
                                        @click="optimizeItinerary"
                                        class="px-4 py-2 text-xs bg-eblue-500 text-white rounded-full font-semibold hover:bg-eblue-600 transition-colors shadow-md shadow-eblue-500/10"
                                    >
                                        {{ s2OptimizedText }}
                                    </button>
                                </div>
                            </div>

                            <!-- STEP 3 CONTENT: Commission Calculator Slider -->
                            <div v-else-if="activeStep === 2" class="space-y-6 w-full py-4">
                                <div class="space-y-2">
                                    <div class="flex justify-between text-sm font-semibold">
                                        <span class="text-eblack-700">{{ $t('marketing.auto.monthly_bookings') }}</span>
                                        <span class="text-eorange-500 font-bold text-lg">{{ s3Bookings }}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="5"
                                        max="100"
                                        step="5"
                                        v-model="s3Bookings"
                                        class="w-full h-2 bg-ewhite-200 rounded-lg appearance-none cursor-pointer accent-eorange-500"
                                    />
                                    <div class="flex justify-between text-[10px] text-eblack-400 font-semibold">
                                        <span>{{ $t('marketing.auto.min_bookings') }}</span>
                                        <span>{{ $t('marketing.auto.max_bookings') }}</span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-2 gap-4 bg-white p-4 rounded-xl border border-ewhite-200 shadow-inner">
                                    <div>
                                        <span class="text-[10px] text-eblack-400 uppercase tracking-wider font-semibold block">{{ $t('marketing.auto.estimated_commission') }}</span>
                                        <span class="text-xl font-bold text-eorange-500">${{ s3Earnings }} USD</span>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <!-- Navigation Indicator -->
                        <div class="flex justify-center gap-1.5 mt-4 pt-3 border-t border-ewhite-200">
                            <span
                                v-for="(step, index) in steps"
                                :key="'dot-' + index"
                                :class="[
                                    'h-1.5 rounded-full transition-all duration-300',
                                    activeStep === index ? 'w-6 bg-eorange-500' : 'w-1.5 bg-ewhite-300'
                                ]"
                            ></span>
                        </div>

                    </div>
                </div>

            </div>

        </div>
    </section>
</template>

<script>
export default {
    name: 'HowItWorksSection',
    data() {
        return {
            activeStep: 0,
            steps: [
                {
                    title: 'how_it_works.steps.step_1.title',
                    description: 'how_it_works.steps.step_1.description',
                },
                {
                    title: 'how_it_works.steps.step_2.title',
                    description: 'how_it_works.steps.step_2.description',
                },
                {
                    title: 'how_it_works.steps.step_3.title',
                    description: 'how_it_works.steps.step_3.description',
                }
            ],

            // Step 1 interactive variables (Synced with WelcomeSection simulator activities)
            s1SelectedCat: 'Todos',
            s1Categories: ['Todos', 'Aventura', 'Bienestar', 'Transporte'],
            s1Services: [
                { nameKey: 'welcome_section.widget.activities.activity_1.name', category: 'Aventura', price: 65, icon: '🌴' },
                { nameKey: 'welcome_section.widget.activities.activity_2.name', category: 'Bienestar', price: 90, icon: '🌋' },
                { nameKey: 'welcome_section.widget.activities.activity_3.name', category: 'Transporte', price: 85, icon: '⛵' }
            ],

            // Step 2 interactive variables
            s2OptimizedText: 'Ordenar Itinerario',
            s2Itinerary: [
                { time: '01:00 PM', activityKey: 'welcome_section.widget.activities.activity_2.name', status: 'Confirmado', optimized: false },
                { time: '08:00 AM', activityKey: 'welcome_section.widget.activities.activity_1.name', status: 'Confirmado', optimized: false },
                { time: '05:00 PM', activityKey: 'welcome_section.widget.activities.activity_3.name', status: 'Pendiente', optimized: false }
            ],

            // Step 3 interactive variables
            s3Bookings: 25
        };
    },
    computed: {
        filteredS1Services() {
            if (this.s1SelectedCat === 'Todos') {
                return this.s1Services;
            }
            // Map category tags
            const catMap = {
                'Aventura': 'Aventura',
                'Bienestar': 'Bienestar',
                'Transporte': 'Transporte'
            };
            return this.s1Services.filter(s => s.category === catMap[this.s1SelectedCat]);
        },
        s3Earnings() {
            // Assume average excursion of $80 with 15% commission ($12.00 per excursion)
            return Math.floor(this.s3Bookings * 80 * 0.15);
        }
    },
    methods: {
        getStepSubHeader() {
            if (this.activeStep === 0) return 'Catálogo de Proveedores';
            if (this.activeStep === 1) return 'Optimizador de Ruta';
            return 'Calculadora de Comisiones';
        },
        optimizeItinerary() {
            if (this.s2OptimizedText === 'Itinerario Optimizado ✓') return;

            // Sort itinerary items chronologically
            this.s2Itinerary = [
                { time: '08:00 AM', activityKey: 'welcome_section.widget.activities.activity_1.name', status: 'Confirmado', optimized: true },
                { time: '01:00 PM', activityKey: 'welcome_section.widget.activities.activity_2.name', status: 'Confirmado', optimized: true },
                { time: '05:00 PM', activityKey: 'welcome_section.widget.activities.activity_3.name', status: 'Confirmado', optimized: true }
            ];
            this.s2OptimizedText = 'Itinerario Optimizado ✓';
            setTimeout(() => {
                this.s2OptimizedText = 'Itinerario Optimizado ✓';
            }, 3000);
        }
    }
}
</script>

<style lang="scss" scoped>
.animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>
